import { useEffect, useState } from "react"
import Bugsnag from "@bugsnag/js"
import _map from "lodash/map"
import _reduce from "lodash/reduce"
import _round from "lodash/round"

import { useAppDispatch, useAppSelector } from "@src/hooks/use-redux"
import useGtm from "@src/hooks/use-gtm"

import { getCartCount, setCartCount } from "@src/redux/cart.slice"
import { getUser } from "@src/redux/user.slice"

import { formatCurrency } from "@src/utilities/format-numbers"

import getCart from "@src/workers/api/get-cart"
import removeFromCart from "@src/workers/api/remove-from-cart"
import updateFrequencyInCart from "@src/workers/api/update-frequency-in-cart"
import updateQuantityInCart from "@src/workers/api/update-quantity-in-cart"

import SplitScreenLayout from "@src/layouts/split-screen"

import CartLineItem from "@src/components/CartLineItem"
import CartEmpty from "@src/components/CartEmpty"
import CartRelatedItem from "@src/components/CartRelatedItem"
import LoadingFullScreen from "@src/components/LoadingFullScreen"

import type { GetFullCartQuery } from "@src/entities/types/api"

export default function CartPage() {
  const dispatch = useAppDispatch()
  const cartCount = useAppSelector(getCartCount)
  const user = useAppSelector(getUser)
  const [cart, setCart] = useState<
    GetFullCartQuery["cart"] | null | undefined
  >()
  const [fullScreenLoaderText, setFullScreenLoaderText] = useState<string>(
    "Loading your basket"
  )
  const [cartIsLoading, setCartIsLoading] = useState<boolean>(true)
  const [cartIsUpdating, setCartIsUpdating] = useState<boolean>(false)

  const onChangeItemFrequency = (
    sku: string,
    oldFrequencyInWeeks: number,
    newFrequencyInWeeks: number
  ) => {
    setFullScreenLoaderText("Updating your cart")
    setCartIsUpdating(true)
    updateFrequencyInCart({
      oldFrequencyInWeeks,
      newFrequencyInWeeks,
      sku,
    }).then(({ data, errors }) => {
      if (errors) {
        _map(errors, (error) => {
          Bugsnag.notify(error)
        })
        setFullScreenLoaderText(
          `Uh oh - we ran into an error. Please refresh and try again.`
        )
        return
      }
      dispatch(
        setCartCount(
          _reduce(
            data?.cartUpdateFrequency.lineItems ?? [],
            (sum, item) => sum + item.quantity,
            0
          )
        )
      )
      setCart(data?.cartUpdateFrequency)
      setCartIsUpdating(false)
    })
  }

  const onChangeItemQuantity = (
    sku: string,
    quantity: number,
    frequency: number | null
  ) => {
    setFullScreenLoaderText("Updating your cart")
    setCartIsUpdating(true)
    updateQuantityInCart({
      frequencyInWeeks: frequency,
      quantity,
      sku,
    }).then(({ data, errors }) => {
      if (errors) {
        _map(errors, (error) => {
          Bugsnag.notify(error)
        })
        setFullScreenLoaderText(
          `Uh oh - we ran into an error. Please refresh and try again.`
        )
        return
      }
      dispatch(
        setCartCount(
          _reduce(
            data?.cartUpdateQuantity.lineItems ?? [],
            (sum, item) => sum + item.quantity,
            0
          )
        )
      )
      setCart(data?.cartUpdateQuantity)
      setCartIsUpdating(false)
    })
  }

  const onRemoveItem = (sku: string, frequency: number | null) => {
    setFullScreenLoaderText("Updating your cart")
    setCartIsUpdating(true)
    removeFromCart({
      frequencyInWeeks: frequency,
      sku,
    }).then(({ data, errors }) => {
      if (errors) {
        _map(errors, (error) => {
          Bugsnag.notify(error)
        })
        setFullScreenLoaderText(
          `Uh oh - we ran into an error. Please refresh and try again.`
        )
        return
      }
      dispatch(
        setCartCount(
          _reduce(
            data?.cartRemoveItem.lineItems ?? [],
            (sum, item) => sum + item.quantity,
            0
          )
        )
      )
      setCart(data?.cartRemoveItem)
      setCartIsUpdating(false)
    })
  }

  useEffect(() => {
    if (cart && cartIsLoading) {
      useGtm({
        event: "orTrack",
        eventName: "Cart Viewed",
        properties: {
          products: _map(cart?.lineItems ?? [], (lineItem) => ({
            ...(lineItem.product.categoryName && {
              category: lineItem.product.categoryName,
            }),
            ...(lineItem.frequencyInWeeks && {
              frequencyInWeeks: lineItem.frequencyInWeeks,
            }),
            ...(lineItem.product.thumbnailUrl && {
              image_url: lineItem.product.thumbnailUrl,
            }),
            name: lineItem.product.name,
            price: _round(0.01 * lineItem.priceInCart, 2),
            product_id: lineItem.product.sku,
            quantity: lineItem.quantity,
            sku: lineItem.product.sku,
            ...(lineItem.product.description && {
              variant: lineItem.product.description,
            }),
            value: _round(lineItem.quantity * 0.01 * lineItem.priceInCart, 2),
          })),
        },
      })
      setCartIsLoading(false)
    }
  }, [cart, cartIsLoading])

  useEffect(() => {
    if (user.authStateKnown) {
      getCart().then(({ data, errors }) => {
        if (errors) {
          _map(errors, (error) => {
            Bugsnag.notify(error)
          })
          setFullScreenLoaderText(
            `Uh oh - we ran into an error. Please refresh and try again.`
          )
          return
        }
        setCart(data.cart)
      })
    }
  }, [user])

  return (
    <SplitScreenLayout
      browserTitle="My Basket | Onyx + Rose"
      isLoading={cartIsLoading}
      loadingText={fullScreenLoaderText}
    >
      <LoadingFullScreen
        showing={cartIsLoading || cartIsUpdating}
        text={fullScreenLoaderText}
      />
      <div className="font-browser md:col-span-3">
        {cart === null || (cart?.lineItems?.length === 0 && <CartEmpty />)}
        {cart?.lineItems && cart.lineItems.length > 0 && (
          <div className="p-4 lg:pr-8 lg:max-w-3xl lg:ml-auto xl:pr-16">
            {cart?.totals?.shipping === 0 && (
              <div className="border border-moss-green bg-moss-green-200 mb-8 p-2 text-forest-green text-sm">
                <p>Your order qualifies for free shipping!</p>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-browser font-extrabold text-2xl uppercase">
                  In my bag
                </h2>
                <a className="text-xs hover:underline" href="/checkout">
                  Check out &rarr;
                </a>
              </div>
              <div className="flex flex-col">
                {_map(cart.lineItems ?? [], (lineItem, idx) => (
                  <CartLineItem
                    key={`line-item-${idx}`}
                    cartPrice={lineItem.priceInCart}
                    description={lineItem.product.description}
                    frequencyInWeeks={lineItem.frequencyInWeeks}
                    name={lineItem.product.name}
                    retailPrice={lineItem.product.priceRetail}
                    quantity={lineItem.quantity}
                    sku={lineItem.product.sku}
                    thumbnailUrl={lineItem.product.thumbnailUrl}
                    onChangeFrequency={onChangeItemFrequency}
                    onChangeQuantity={onChangeItemQuantity}
                    onRemoveItem={onRemoveItem}
                  />
                ))}
                <div className="xl:mt-4 xl:mb-4">
                  <h2 className="font-browser font-medium mb-2 text-xl">
                    My Totals
                  </h2>
                  <div className="grid grid-cols-1 divide-y">
                    <div className="grid grid-cols-2 py-1">
                      <div className="font-medium">Subtotal</div>
                      <div
                        className="font-medium text-right"
                        dangerouslySetInnerHTML={{
                          __html: formatCurrency(cart.totals.subtotal),
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 py-1">
                      <div className="font-medium">Discounts</div>
                      <div
                        className="font-medium text-right"
                        dangerouslySetInnerHTML={{
                          __html:
                            cart.totals.discounts > 0
                              ? formatCurrency(cart.totals.discounts)
                              : "&mdash;",
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 py-1">
                      <div className="font-medium">Shipping</div>
                      <div
                        className="font-medium text-right"
                        dangerouslySetInnerHTML={{
                          __html:
                            cart.totals.shipping > 0
                              ? formatCurrency(cart.totals.shipping)
                              : "Free!",
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 py-2">
                      <div className="text-gray-600 text-sm">Taxes</div>
                      <div className="text-gray-600 text-sm text-right">
                        Calculated at checkout
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-16 mt-4 lg:mt-8">
                  <div className="md:flex md:items-center md:justify-between md:flex-col-reverse lg:flex-row">
                    <a
                      className="font-browser font-normal text-gray-600 text-sm hidden md:inline-block md:mt-8 lg:mt-0 hover:underline"
                      href="/shop"
                    >
                      &larr; Continue shopping
                    </a>
                    <a
                      className="bg-orange block md:inline-flex font-browser font-medium px-8 py-2 text-center text-white uppercase w-full md:w-auto hover:bg-orange-600 transition-colors"
                      href="/checkout"
                    >
                      Continue to Check out &rarr;
                    </a>
                  </div>
                </div>
                <div className="hidden md:hidden">
                  <h3 className="font-browser font-medium mb-4 text-xl">
                    You may also be interested in...
                  </h3>
                  {_map(Array.from(Array(3).keys()), (key, idx) => (
                    <CartRelatedItem
                      key={`related-item-${idx}`}
                      cartPrice={2900}
                      description="Awesome description"
                      name="Product Name"
                      retailPrice={2900}
                      sku="fakesku"
                      thumbnailUrl="/images/placeholder.jpeg"
                      onQuickAdd={(sku: string) => {
                        console.log(sku)
                      }}
                    />
                  ))}
                  <div className="mb-16 mt-16">
                    <a
                      className="font-browser font-medium text-orange"
                      href="/shop"
                    >
                      &larr; Continue shopping
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="font-browser bg-gray-200 border-l border-gray-300 hidden p-4 md:block md:col-span-2 lg:px-8">
        {cart === null || (cart?.lineItems?.length === 0 && <span>{""}</span>)}
        {""}
        {cart?.lineItems && cart.lineItems.length > 0 && (
          <div className="hidden md:hidden">
            <h3 className="font-browser font-medium mb-4 text-lg">
              You may also be interested in...
            </h3>
            {_map(Array.from(Array(3).keys()), (key, idx) => (
              <CartRelatedItem
                key={`related-item-${idx}`}
                cartPrice={2900}
                description="Awesome description"
                name="Product Name"
                retailPrice={2900}
                sku="fakesku"
                thumbnailUrl="/images/placeholder.jpeg"
                onQuickAdd={(sku: string) => {
                  console.log(sku)
                }}
              />
            ))}
          </div>
        )}
      </div>
    </SplitScreenLayout>
  )
}
